import type { Player, Round, Score, TeeSet } from "../types.js";
import type { Contest, Format, RoundResult } from "./types.js";
import {
  buildScoreMap,
  computeHandicaps,
  holeCount,
  listNames,
  netOnHole,
  scoringHoles,
} from "./util.js";

interface WolfPoints {
  partnerWin: number;
  huntersWin: number;
  loneWolfWin: number;
  loneWolfLoss: number;
}

type RotationStrategy = "rotate" | "trailing" | "manual";

interface WolfConfig {
  points?: Partial<WolfPoints>;
  blindMultiplier?: number;
  rotationStrategy?: RotationStrategy;
}

const DEFAULT_POINTS: WolfPoints = {
  partnerWin: 2,
  huntersWin: 3,
  loneWolfWin: 4,
  loneWolfLoss: 1,
};

/** One hole of Wolf, as the walk saw it. */
interface WolfHole {
  hole: number;
  wolfId: string;
  partnerId: string | null;
  blind: boolean;
  lone: boolean;
  wolfSide: string[];
  huntersSide: string[];
  /** Best net on each side, or null while the hole is short of a score. */
  wolfBest: number | null;
  huntersBest: number | null;
  winner: "wolf" | "hunters" | null;
  /** Why there is no winner, when there isn't one. */
  state: "decided" | "push" | "incomplete";
  multiplier: number;
  /** What the hole paid, by player id. Empty unless `state` is "decided". */
  points: Record<string, number>;
}

/**
 * The single walk through a Wolf round.
 *
 * `score` and `explainHoles` each used to re-derive the sides, the best nets and the
 * payout from scratch. Two copies of a rule drift — the Skins walk exists because
 * exactly that happened there — and a third copy for the hole-by-hole contest would
 * have made it three.
 *
 * A hole with no Wolf recorded simply isn't in the result: there was no contest on it.
 */
function walkHoles(
  round: Round,
  course: TeeSet,
  players: Player[],
  scores: Score[],
  perHole: Record<string, unknown>[],
  onlyHoles: number[] | undefined,
): WolfHole[] {
  const config = (round.formatConfig ?? {}) as WolfConfig;
  const pointsCfg: WolfPoints = { ...DEFAULT_POINTS, ...(config.points ?? {}) };
  const blindMultiplier = config.blindMultiplier ?? 2;

  const scoreMap = buildScoreMap(scores);
  const handicaps = computeHandicaps(round, course, players);
  const allPlayerIds = players.map((p) => p.id);
  // Narrowed to the round's own holes before anything else: a per-hole record
  // survives a round being switched from 18 holes to a nine, and scoring it would
  // pay out on a hole this round never played.
  const inPlay = new Set(scoringHoles(round, course, onlyHoles));

  const walked: WolfHole[] = [];

  for (const entry of perHole) {
    const hole = entry.hole as number | undefined;
    const wolfId = entry.wolfId as string | undefined;
    const partnerId = (entry.partnerId as string | null | undefined) ?? null;
    const blind = Boolean(entry.blind);
    if (hole == null || !wolfId) continue;
    if (!inPlay.has(hole)) continue;

    const lone = partnerId == null;
    const wolfSide = lone ? [wolfId] : [wolfId, partnerId];
    const huntersSide = allPlayerIds.filter((id) => !wolfSide.includes(id));
    // With no hunters left (e.g. only 2 players total and the Wolf takes the only
    // other player as partner), Math.min(...[]) below would be +Infinity and the
    // Wolf's side would "win" the hole by default regardless of actual scores.
    if (huntersSide.length === 0) continue;

    const multiplier = blind ? blindMultiplier : 1;
    const base: WolfHole = {
      hole,
      wolfId,
      partnerId,
      blind,
      lone,
      wolfSide,
      huntersSide,
      wolfBest: null,
      huntersBest: null,
      winner: null,
      state: "incomplete",
      multiplier,
      points: {},
    };

    const netFor = (id: string) => netOnHole(scoreMap, handicaps, id, hole);
    const wolfNets = wolfSide.map(netFor).filter((n): n is number => n != null);
    const hunterNets = huntersSide.map(netFor).filter((n): n is number => n != null);
    if (wolfNets.length < wolfSide.length || hunterNets.length < huntersSide.length) {
      walked.push(base);
      continue;
    }

    const wolfBest = Math.min(...wolfNets);
    const huntersBest = Math.min(...hunterNets);
    if (wolfBest === huntersBest) {
      walked.push({ ...base, wolfBest, huntersBest, state: "push" }); // no points either way
      continue;
    }

    const points: Record<string, number> = {};
    const wolfSideWon = wolfBest < huntersBest;
    if (wolfSideWon) {
      if (lone) points[wolfId] = pointsCfg.loneWolfWin * multiplier;
      else for (const id of wolfSide) points[id] = pointsCfg.partnerWin * multiplier;
    } else {
      const each = (lone ? pointsCfg.loneWolfLoss : pointsCfg.huntersWin) * multiplier;
      for (const id of huntersSide) points[id] = each;
    }

    walked.push({
      ...base,
      wolfBest,
      huntersBest,
      winner: wolfSideWon ? "wolf" : "hunters",
      state: "decided",
      points,
    });
  }

  return walked.sort((x, y) => x.hole - y.hole);
}

/** How a hole's side reads on the card: "Lone Wolf: Sam", "Wolf: Sam + Alex". */
function wolfSideLabel(hole: WolfHole, nameOf: (id: string) => string): string {
  return hole.lone
    ? `Lone Wolf: ${nameOf(hole.wolfId)}`
    : `Wolf: ${nameOf(hole.wolfId)} + ${nameOf(hole.partnerId!)}`;
}

export const wolf: Format = {
  id: "wolf",
  name: "Wolf",
  scope: "individual",
  uses: { handicapAllowance: true },
  entry: "perPlayer",
  defaults: { handicapAllowance: 100 },
  configSchema: {
    type: "object",
    properties: {
      points: {
        type: "object",
        title: "Hole point values",
        properties: {
          partnerWin: { type: "number", default: 2, title: "Wolf + partner win (each)" },
          huntersWin: { type: "number", default: 3, title: "Hunters win (each)" },
          loneWolfWin: { type: "number", default: 4, title: "Lone Wolf wins" },
          loneWolfLoss: { type: "number", default: 1, title: "Lone Wolf loses (each hunter)" },
        },
      },
      blindMultiplier: {
        type: "number",
        default: 2,
        title: "Blind Wolf multiplier",
        description: "Multiplies that hole's points when the Wolf goes blind before any tee shot.",
      },
      rotationStrategy: {
        type: "string",
        enum: ["rotate", "trailing", "manual"],
        default: "trailing",
        title: "Wolf rotation",
        description:
          "Once everyone has been Wolf an equal number of times, 'trailing' gives the remaining holes to whoever is behind on Wolf points.",
      },
    },
  },
  perHoleSchema: {
    type: "object",
    properties: {
      wolfId: { type: "string" },
      partnerId: { type: ["string", "null"] },
      blind: { type: "boolean", default: false },
    },
    required: ["wolfId"],
  },

  score({ round, course, players, scores, perHole, onlyHoles }): RoundResult {
    const points: Record<string, number> = {};
    for (const p of players) points[p.id] = 0;

    for (const hole of walkHoles(round, course, players, scores, perHole, onlyHoles)) {
      for (const [id, gained] of Object.entries(hole.points)) {
        points[id] = (points[id] ?? 0) + gained;
      }
    }

    return { roundId: round.id, points, provisional: Boolean(onlyHoles) };
  },

  /** Which side this player was on each hole and what it paid. Wolf's points depend
   * entirely on a per-hole pairing the totals can't show. */
  explainHoles({ round, course, players, scores, perHole, onlyHoles, playerId }) {
    const config = (round.formatConfig ?? {}) as WolfConfig;
    const blindMultiplier = config.blindMultiplier ?? 2;
    const notes = new Map<number, string>();

    for (const hole of walkHoles(round, course, players, scores, perHole, onlyHoles)) {
      const onWolfSide = hole.wolfSide.includes(playerId);
      const role = hole.wolfId === playerId ? "Wolf" : onWolfSide ? "Wolf's partner" : "hunter";
      const blindNote = hole.blind ? `, blind ×${blindMultiplier}` : "";
      const prefix = `${role}${blindNote}`;

      switch (hole.state) {
        case "incomplete":
          notes.set(hole.hole, `${prefix} — not all scores in`);
          break;
        case "push":
          notes.set(hole.hole, `${prefix} — halved, no points`);
          break;
        case "decided": {
          const gained = hole.points[playerId] ?? 0;
          notes.set(
            hole.hole,
            gained > 0 ? `${prefix} — won, +${gained}` : `${prefix} — lost, no points`,
          );
          break;
        }
      }
    }
    return notes;
  },

  /** Wolf's sides re-form on every tee, so the contest has no fixed columns: each hole
   * carries the pairing that actually applied to it. */
  explainContests({ round, course, players, scores, perHole, onlyHoles }): Contest[] {
    const config = (round.formatConfig ?? {}) as WolfConfig;
    const blindMultiplier = config.blindMultiplier ?? 2;
    const nameOf = (id: string) => players.find((p) => p.id === id)?.name ?? id;
    const walked = walkHoles(round, course, players, scores, perHole, onlyHoles);
    if (walked.length === 0) return [];

    const totals: Record<string, number> = {};
    for (const p of players) totals[p.id] = 0;
    for (const hole of walked) {
      for (const [id, gained] of Object.entries(hole.points)) {
        totals[id] = (totals[id] ?? 0) + gained;
      }
    }

    const holes = walked.map((hole) => {
      const blindNote = hole.blind ? `blind ×${blindMultiplier}` : null;
      const paid =
        hole.state === "incomplete"
          ? "not all scores in"
          : hole.state === "push"
            ? "halved, no points"
            : hole.winner === "wolf"
              ? `+${hole.points[hole.wolfId] ?? 0} to the Wolf's side`
              : `+${hole.points[hole.huntersSide[0]!] ?? 0} each to the hunters`;

      return {
        hole: hole.hole,
        sides: [
          {
            id: "wolf",
            label: wolfSideLabel(hole, nameOf),
            playerIds: hole.wolfSide,
            value: hole.wolfBest,
            won: hole.winner === "wolf",
          },
          {
            id: "hunters",
            label: `Hunters: ${hole.huntersSide.map(nameOf).join(", ")}`,
            playerIds: hole.huntersSide,
            value: hole.huntersBest,
            won: hole.winner === "hunters",
          },
        ],
        note: [blindNote, paid].filter(Boolean).join(" — "),
      };
    });

    const best = Math.max(...Object.values(totals));
    const leaders = Object.entries(totals)
      .filter(([, v]) => v === best)
      .map(([id]) => nameOf(id));
    const standing =
      best === 0
        ? "Nobody on the board yet"
        : leaders.length === 1
          ? `${leaders[0]} leads on ${best}`
          : `${listNames(leaders)} tied on ${best}`;

    return [{ id: "wolf", title: "Wolf", columns: null, holes, standing }];
  },

  /**
   * Wolf is a four-player game: one Wolf, one possible partner, and the rest hunting.
   * There is no notion of a playing group in the data model, so on a bigger roster
   * *everyone* else is a hunter and a single lost hole pays out to all of them. That
   * scales the round's points with the field size, which is rarely what an organiser
   * running two groups expects — so say so rather than let it be discovered in the
   * standings.
   */
  validate(_round, players) {
    if (players.length <= 5) return [];
    return [
      {
        level: "warning",
        message: `Wolf is a 3-5 player game, and this round has ${players.length}. Every player who isn't the Wolf or their partner is a hunter, so a lost hole pays ${players.length - 1} players at once. Consider a separate round per group.`,
      },
    ];
  },

  describeRules(round, players, course) {
    const config = (round.formatConfig ?? {}) as WolfConfig;
    const pointsCfg: WolfPoints = { ...DEFAULT_POINTS, ...(config.points ?? {}) };
    const blindMultiplier = config.blindMultiplier ?? 2;
    const rotationStrategy = config.rotationStrategy ?? "trailing";
    const hunters = Math.max(1, players.length - 2);
    return [
      `Wolf across ${holeCount(round, course)} holes. The rotating Wolf tees off first, watches each opponent's drive, and immediately chooses that player as a partner or passes; a pass is final for the hole.`,
      "If the Wolf passes on everyone, they play Lone Wolf, 1 vs the rest.",
      `Everyone not on the Wolf's side is a hunter — ${hunters} of them with ${players.length} playing — and they all score together.`,
      `Points: Wolf + partner win ${pointsCfg.partnerWin} each; hunters win ${pointsCfg.huntersWin} each; Lone Wolf wins ${pointsCfg.loneWolfWin}; Lone Wolf loses ${pointsCfg.loneWolfLoss} to each hunter.`,
      `Blind Wolf (declared before any tee shot) multiplies that hole's points by ${blindMultiplier}.`,
      `Wolf rotation: ${rotationStrategy}` +
        (rotationStrategy === "trailing"
          ? " — once the natural rotation completes, remaining holes go to whoever is currently trailing in Wolf points, ties broken by reverse rotation order."
          : "."),
    ];
  },
};

/**
 * UI helper (not part of the Format interface): suggests who should be Wolf on a
 * given hole under the round's configured rotation strategy. Always overridable —
 * this only drives the default the scorecard proposes.
 */
export function suggestNextWolf(
  order: string[],
  strategy: RotationStrategy,
  totalHoles: number,
  holeIndex: number,
  currentPoints: Record<string, number>,
): string | null {
  if (strategy === "manual" || order.length === 0) return null;
  if (strategy === "rotate") return order[holeIndex % order.length] ?? null;

  const naturalHoles = order.length * Math.floor(totalHoles / order.length);
  if (holeIndex < naturalHoles) return order[holeIndex % order.length] ?? null;

  let trailingId: string | null = null;
  let trailingPoints = Infinity;
  for (let i = 0; i < order.length; i++) {
    const id = order[i]!;
    const pts = currentPoints[id] ?? 0;
    if (pts <= trailingPoints) {
      trailingPoints = pts;
      trailingId = id;
    }
  }
  return trailingId;
}
